import { useBrokerSignInMutation } from "Auth/Api/Mutations/useBrokerSignInMutation";
import { JWT_KEY } from "Auth/Models/AuthConstants";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { Loader2 } from "Shared/appIcons";
import { UnauthenticatedLayout } from "Shared/Layouts/UnauthenticatedLayout";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

const PageResources = Resources.BrokerAuth;

type BrokerParams =
	| { kind: "missing" }
	| { kind: "ready"; userToken: string; originUrl: string };

const readBrokerParams = (): BrokerParams => {
	const params = new URLSearchParams(window.location.search);
	const userToken = params.get("userToken");

	if (!userToken) {
		return { kind: "missing" };
	}

	return {
		kind: "ready",
		userToken,
		originUrl: params.get("originUrl") ?? "",
	};
};

export const BrokerAuthPage = () => {
	const navigate = useNavigate();
	const { t } = useResource();
	const { mutate, error } = useBrokerSignInMutation();

	// Parse the URL once on mount — the effect below clears the query string
	// to prevent token replay via the back button, so we cannot re-derive this
	// on subsequent renders.
	const [brokerParams] = useState<BrokerParams>(readBrokerParams);

	// One-shot guard: broker tokens are single-use, so the mutation must fire
	// exactly once even if the effect re-runs (React.StrictMode double-invoke
	// in dev, fast refresh, etc.).
	const dispatchedRef = useRef(false);

	useEffect(() => {
		if (brokerParams.kind !== "ready") return;
		if (dispatchedRef.current) return;
		dispatchedRef.current = true;

		// Clear URL parameters immediately to prevent token reuse via back button.
		window.history.replaceState(null, "", window.location.pathname);

		// Drop any stale JWT before the broker handshake so a failed broker
		// sign-in can't leave the user authenticated as someone else.
		localStorage.removeItem(JWT_KEY);

		mutate(
			{
				body: {
					token: brokerParams.userToken,
					originUrl: brokerParams.originUrl,
				},
			},
			{
				onSuccess: () => {
					navigate(AppRouting.Dashboard.path(), { replace: true });
				},
			},
		);
	}, [brokerParams, mutate, navigate]);

	const showError = brokerParams.kind === "missing" || Boolean(error);
	const errorMessage =
		brokerParams.kind === "missing"
			? t(PageResources.Error.MissingToken)
			: t(PageResources.Error.General);

	return (
		<UnauthenticatedLayout title={t(PageResources.Title)}>
			<div className="flex min-h-40 w-full max-w-sm flex-col items-center justify-center gap-4">
				{showError ? (
					<p className="text-center text-base text-destructive">
						{errorMessage}
					</p>
				) : (
					<>
						<Loader2
							className="size-12 animate-spin text-muted-foreground"
							aria-hidden="true"
						/>
						<p className="text-center text-label text-muted-foreground">
							{t(PageResources.InProgress)}
						</p>
					</>
				)}
			</div>
		</UnauthenticatedLayout>
	);
};
